// Audit Logging Service for PCI DSS Compliance
import { supabase } from '../lib/supabase';

class AuditService {
  // Log payment-related events (PCI DSS requirement)
  async logPaymentEvent(eventType, details, success = true, errorMessage = null) {
    try {
      const { data, error } = await supabase.rpc('log_security_event', {
        p_event_type: eventType,
        p_event_category: 'payment',
        p_event_details: details,
        p_severity: success ? 'info' : 'error',
        p_success: success,
        p_error_message: errorMessage,
        p_payment_id: details.payment_id || null,
        p_payment_amount: details.amount || null,
        p_pci_relevant: true
      });

      if (error) console.error('Audit log error:', error);
      return data;
    } catch (err) {
      console.error('Failed to log payment event:', err);
    }
  }

  // Log authentication events
  async logAuthEvent(eventType, details, success = true) {
    try {
      const { data, error } = await supabase
        .from('login_attempts')
        .insert({
          email: details.email,
          ip_address: details.ip || null,
          user_agent: navigator.userAgent,
          success: success,
          failure_reason: details.failure_reason || null,
          two_factor_used: details.two_factor_used || false
        });

      if (error) console.error('Auth log error:', error);
      
      // Also log to main audit table
      await supabase.rpc('log_security_event', {
        p_event_type: eventType,
        p_event_category: 'auth',
        p_event_details: details,
        p_severity: success ? 'info' : 'warning',
        p_success: success
      });

      return data;
    } catch (err) {
      console.error('Failed to log auth event:', err);
    }
  }

  // Log data access events
  async logDataAccess(resource, action, details = {}) {
    try {
      const { data, error } = await supabase.rpc('log_security_event', {
        p_event_type: `${resource}_${action}`,
        p_event_category: 'access',
        p_event_details: {
          resource,
          action,
          ...details
        },
        p_severity: 'info',
        p_success: true
      });

      if (error) console.error('Access log error:', error);
      return data;
    } catch (err) {
      console.error('Failed to log access event:', err);
    }
  }

  // Log admin actions
  async logAdminAction(action, details, severity = 'info') {
    try {
      const { data, error } = await supabase.rpc('log_security_event', {
        p_event_type: `admin_${action}`,
        p_event_category: 'admin',
        p_event_details: details,
        p_severity: severity,
        p_success: true
      });

      if (error) console.error('Admin log error:', error);
      return data;
    } catch (err) {
      console.error('Failed to log admin action:', err);
    }
  }

  // Log security incidents
  async logSecurityIncident(incidentType, details, severity = 'warning') {
    try {
      const { data, error } = await supabase.rpc('log_security_event', {
        p_event_type: `security_${incidentType}`,
        p_event_category: 'security',
        p_event_details: details,
        p_severity: severity,
        p_success: false,
        p_error_message: details.message || 'Security incident detected'
      });

      if (error) console.error('Security incident log error:', error);
      
      // Alert admins for critical incidents
      if (severity === 'critical') {
        this.alertAdmins(incidentType, details);
      }
      
      return data;
    } catch (err) {
      console.error('Failed to log security incident:', err);
    }
  }

  // Alert admins of critical issues
  async alertAdmins(incidentType, details) {
    // This would integrate with your SMS/email service
    console.error('CRITICAL SECURITY INCIDENT:', incidentType, details);
    // TODO: Send SMS/email to admins
  }

  // Get audit logs (admin only)
  async getAuditLogs(filters = {}) {
    try {
      let query = supabase
        .from('security_audit_logs')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters.category) {
        query = query.eq('event_category', filters.category);
      }
      if (filters.start_date) {
        query = query.gte('created_at', filters.start_date);
      }
      if (filters.end_date) {
        query = query.lte('created_at', filters.end_date);
      }
      if (filters.pci_only) {
        query = query.eq('pci_relevant', true);
      }

      const { data, error } = await query.limit(filters.limit || 100);

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Failed to get audit logs:', err);
      return [];
    }
  }

  // Session management
  async createSession(userId, token, expiresIn = 3600) {
    try {
      const expiresAt = new Date(Date.now() + expiresIn * 1000);
      
      const { data, error } = await supabase
        .from('user_sessions')
        .insert({
          user_id: userId,
          session_token: token,
          ip_address: null, // Would get from request in production
          user_agent: navigator.userAgent,
          expires_at: expiresAt
        });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Failed to create session:', err);
    }
  }

  async updateSessionActivity(sessionToken) {
    try {
      const { error } = await supabase
        .from('user_sessions')
        .update({ last_activity: new Date() })
        .eq('session_token', sessionToken)
        .eq('is_active', true);

      if (error) throw error;
    } catch (err) {
      console.error('Failed to update session:', err);
    }
  }

  async terminateSession(sessionToken, reason = 'user_logout') {
    try {
      const { error } = await supabase
        .from('user_sessions')
        .update({ 
          is_active: false,
          terminated_reason: reason 
        })
        .eq('session_token', sessionToken);

      if (error) throw error;
    } catch (err) {
      console.error('Failed to terminate session:', err);
    }
  }

  // Check for expired sessions
  async checkSessionTimeout(sessionToken, maxInactivityMinutes = 30) {
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('last_activity, expires_at')
        .eq('session_token', sessionToken)
        .eq('is_active', true)
        .single();

      if (error || !data) return false;

      const now = new Date();
      const lastActivity = new Date(data.last_activity);
      const expiresAt = new Date(data.expires_at);
      const inactivityTime = (now - lastActivity) / (1000 * 60); // minutes

      if (now > expiresAt || inactivityTime > maxInactivityMinutes) {
        await this.terminateSession(sessionToken, 'timeout');
        return false;
      }

      return true;
    } catch (err) {
      console.error('Failed to check session timeout:', err);
      return false;
    }
  }
}

export const auditService = new AuditService();
